import React from 'react';
import { Facebook, Instagram, Clock, MessageCircle } from 'lucide-react';
import { mockData } from '../mock';

export const Footer = ({ dealershipName }) => {
  const currentYear = new Date().getFullYear();
  const openingHours = mockData.dealership.openingHours;

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Opening Hours Section */}
        <div className="mb-12 pb-8 border-b border-gray-700">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold">Öffnungszeiten</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Verkauf */}
            <div className="bg-white/5 rounded-xl backdrop-blur-sm p-6" data-testid="footer-hours-verkauf">
              <p className="text-center text-amber-300 font-semibold uppercase tracking-wider text-sm mb-4">
                Verkauf
              </p>
              <div className="space-y-2">
                {openingHours.verkauf.map((schedule, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center py-2 border-b border-white/10 last:border-0"
                  >
                    <span className="text-gray-300 font-medium">{schedule.days}</span>
                    <span className="text-white font-semibold">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Werkstatt / Service */}
            <div className="bg-white/5 rounded-xl backdrop-blur-sm p-6" data-testid="footer-hours-werkstatt">
              <p className="text-center text-amber-300 font-semibold uppercase tracking-wider text-sm mb-4">
                Werkstatt &amp; Service
              </p>
              <div className="space-y-2">
                {openingHours.werkstatt.map((schedule, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center py-2 border-b border-white/10 last:border-0"
                  >
                    <span className="text-gray-300 font-medium">{schedule.days}</span>
                    <span className="text-white font-semibold">{schedule.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_wilke-auto-portal/artifacts/sdbsg346_fixed_autohaus-wilke.ai_black.png"
              alt={dealershipName}
              className="h-20 w-auto object-contain mb-4"
              style={{ 
                maxWidth: '280px',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <p className="text-gray-400 leading-relaxed">
              Ihr vertrauenswürdiger Partner für Nissan, Mazda, Omoda und Jaecoo in Magdeburg.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Schnelllinks</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://wilke.haendler-nissan.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Nissan Händlerseite
                </a>
              </li>
              <li>
                <a
                  href="https://www.mazda-autohaus-wilke-magdeburg.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Mazda Händlerseite
                </a>
              </li>
              <li>
                <a
                  href="https://www.google.com/maps/dir//Autohaus+Wilke+Magdeburg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Route planen
                </a>
              </li>
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Folgen Sie uns</h4>
            <div className="flex gap-4 mb-6">
              <a
                href="https://www.facebook.com/autohaus.wilke"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Autohaus Wilke auf Facebook"
                className="w-10 h-10 bg-white/10 hover:bg-[#1877F2] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/autohauswilke/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Autohaus Wilke auf Instagram"
                className="w-10 h-10 bg-white/10 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#F77737] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://wa.me/4939163577134?text=Hallo,%20ich%20interessiere%20mich%20für%20ein%20Fahrzeug"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp Kontakt"
                data-testid="footer-whatsapp"
                className="w-10 h-10 bg-white/10 hover:bg-[#25D366] rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
            <p className="text-gray-400 text-sm">
              WhatsApp:{' '}
              <a
                href="https://wa.me/4939163577134"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-green-400 transition-colors font-medium"
              >
                +49 391 6357 7134
              </a>
            </p>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} {dealershipName}. Alle Rechte vorbehalten.
            </p>
            <div className="flex gap-6 text-sm">
              <a
                href="/impressum.html"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                Impressum
              </a>
              <a
                href="/datenschutz.html"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                Datenschutz & AGB
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
